// src/modules/assetManagement/components/Breadcrumbs.tsx
// Session trail for the Library (Events › Event › Product › Asset); Back returns one step.
import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronRight } from "lucide-react";
import type { Crumb } from "../types";
import { trailFrom } from "../utils";

/** The trail to pass when navigating onward from the current page */
export const useTrail = (current: Crumb) => {
  const location = useLocation();
  const previous = trailFrom(location.state);
  return { previous, next: [...previous, current] };
};

const Breadcrumbs: React.FC<{ current: string; fallback: Crumb }> = ({ current, fallback }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const trail = trailFrom(location.state);
  const crumbs = trail.length ? trail : [fallback];

  const goBack = () => {
    // Prefer history so the browser and this button stay in step
    if (trail.length && window.history.state?.idx > 0) navigate(-1);
    else navigate(crumbs[crumbs.length - 1].to, { state: { trail: crumbs.slice(0, -1) } });
  };

  return (
    <div className="flex items-center gap-3 mb-5 min-w-0">
      <button
        type="button"
        onClick={goBack}
        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-sm text-slate-700 hover:bg-slate-50"
      >
        <ArrowLeft size={14} /> Back
      </button>
      <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-sm text-slate-500 min-w-0 overflow-x-auto whitespace-nowrap">
        {crumbs.map((c, i) => (
          <React.Fragment key={`${c.to}-${i}`}>
            <Link to={c.to} state={{ trail: crumbs.slice(0, i) }} className="hover:text-slate-800 hover:underline">
              {c.label}
            </Link>
            <ChevronRight size={14} className="shrink-0 text-slate-300" />
          </React.Fragment>
        ))}
        <span className="text-slate-800 font-medium truncate">{current}</span>
      </nav>
    </div>
  );
};

export default Breadcrumbs;
